/**
 * Stage intro and completion animation orchestration between questionnaire stages.
 *
 * Belongs here: single `bodyPhase` machine, when to play intro/leave/complete, Body callbacks.
 * Does not belong here: answer handling, API submit, or question rendering.
 */
import { useEffect, useRef, useState } from 'react'
import { lightImpactVibrate } from '../../../../../utils/appBridge'
import type { StrategyQuestionnaireStage } from '../../shared/types/questionnaire.types'
import { STAGE_COMPLETE_RIVE_START_AFTER_MS, STAGE_COMPLETION_RIVE_SRC } from '../setup/config'
import { getStageIntroRiveSrc } from '../setup/stageAssets'

export interface StrategyEntryFlags {
  isEditFromReview: boolean
  playStageAnimations: boolean
  completeWithStackPop: boolean
  shouldOfferStaticIntro: boolean
}

/**
 * Body slot phases — one source of truth (no parallel “holding” flag).
 * Questions → QuestionsLeaving → StageComplete → (advance) → StageIntro? → Questions
 */
export enum QuestionnaireBodyPhase {
  Questions = 'questions',
  QuestionsLeaving = 'questions-leaving',
  StageIntro = 'stage-intro',
  StageComplete = 'stage-complete'
}

/** Animation-only payloads — answers live in the session / Leave plan, never here. */
export enum StageFinishedType {
  NextStage = 'next_stage',
  LeaveToReview = 'leave_to_review'
}

export type StageFinishedPayload =
  | { type: StageFinishedType.NextStage; nextStageIndex: number }
  | { type: StageFinishedType.LeaveToReview }

export interface UseStageAnimationsParams {
  entryFlags: StrategyEntryFlags
  currentStageId: string | undefined
  stages: StrategyQuestionnaireStage[] | undefined
  onAdvanceToStage: (nextStageIndex: number) => void
  /** After finish animation — page calls leaveQuestionnaire with the stashed Leave plan. */
  onLeaveToReview: () => void
}

export interface StageAnimations {
  bodyPhase: QuestionnaireBodyPhase
  stageIntroRiveSrc: string | undefined
  stageIntroStageId: string | undefined
  isShowingStageIntro: boolean
  isShowingStageComplete: boolean
  /** Last question is sliding out; ignore duplicate answers / cancel resets to Questions. */
  isLeavingQuestions: boolean
  onStageFinished: (payload: StageFinishedPayload) => void
  cancelAnimations: () => void
  onStageIntroComplete: () => void
  onStageCompleteRiveDone: () => void
}

export function useStageAnimations({
  entryFlags,
  currentStageId,
  stages,
  onAdvanceToStage,
  onLeaveToReview
}: UseStageAnimationsParams): StageAnimations {
  const { playStageAnimations, isEditFromReview } = entryFlags

  const [bodyPhase, setBodyPhase] = useState(QuestionnaireBodyPhase.Questions)
  const [stageIntroRiveSrc, setStageIntroRiveSrc] = useState<string | undefined>(undefined)
  const [stageIntroStageId, setStageIntroStageId] = useState<string | undefined>(undefined)

  const stageIntroPlayedRef = useRef(new Set<string>())
  const pendingAfterCompletionRiveRef = useRef<(() => void) | null>(null)
  const pendingRiveStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasScheduledInitialStageIntroRef = useRef(false)

  const onAdvanceToStageRef = useRef(onAdvanceToStage)
  const onLeaveToReviewRef = useRef(onLeaveToReview)
  const stagesRef = useRef(stages)
  onAdvanceToStageRef.current = onAdvanceToStage
  onLeaveToReviewRef.current = onLeaveToReview
  stagesRef.current = stages

  function clearRiveStartTimeout() {
    if (pendingRiveStartTimeoutRef.current == null) return
    clearTimeout(pendingRiveStartTimeoutRef.current)
    pendingRiveStartTimeoutRef.current = null
  }

  function shouldPlayStageIntro(stageId: string | undefined) {
    if (!playStageAnimations || !stageId) return false
    if (stageIntroPlayedRef.current.has(stageId)) return false
    return getStageIntroRiveSrc(stageId) != null
  }

  function beginStageIntro(stageId: string) {
    const riveSrc = getStageIntroRiveSrc(stageId)
    if (!riveSrc) {
      setStageIntroStageId(undefined)
      setBodyPhase(QuestionnaireBodyPhase.Questions)
      return
    }
    stageIntroPlayedRef.current.add(stageId)
    setStageIntroStageId(stageId)
    setStageIntroRiveSrc(riveSrc)
    setBodyPhase(QuestionnaireBodyPhase.StageIntro)
  }

  useEffect(() => {
    if (hasScheduledInitialStageIntroRef.current || !playStageAnimations) return
    if (!currentStageId) return
    hasScheduledInitialStageIntroRef.current = true
    if (shouldPlayStageIntro(currentStageId)) {
      beginStageIntro(currentStageId)
    }
  }, [currentStageId, playStageAnimations])

  useEffect(() => {
    if (bodyPhase !== QuestionnaireBodyPhase.StageIntro) return
    if (stageIntroRiveSrc) return
    setBodyPhase(QuestionnaireBodyPhase.Questions)
  }, [bodyPhase, stageIntroRiveSrc])

  useEffect(() => {
    if (bodyPhase !== QuestionnaireBodyPhase.QuestionsLeaving) return
    const preloadLink = document.createElement('link')
    preloadLink.rel = 'preload'
    preloadLink.as = 'fetch'
    preloadLink.href = STAGE_COMPLETION_RIVE_SRC
    preloadLink.crossOrigin = 'anonymous'
    document.head.appendChild(preloadLink)
    return () => {
      preloadLink.remove()
    }
  }, [bodyPhase])

  useEffect(() => {
    return () => clearRiveStartTimeout()
  }, [])

  function onStageIntroComplete() {
    setStageIntroRiveSrc(undefined)
    setStageIntroStageId(undefined)
    setBodyPhase(QuestionnaireBodyPhase.Questions)
  }

  function onStageCompleteRiveDone() {
    lightImpactVibrate()
    const advance = pendingAfterCompletionRiveRef.current
    pendingAfterCompletionRiveRef.current = null
    advance?.()
  }

  function cancelAnimations() {
    clearRiveStartTimeout()
    pendingAfterCompletionRiveRef.current = null
    setStageIntroRiveSrc(undefined)
    setStageIntroStageId(undefined)
    setBodyPhase(QuestionnaireBodyPhase.Questions)
  }

  /**
   * User finished the last visible question in a stage (or the whole questionnaire).
   *
   * Contract: answerQuestion already committed / built Leave before this runs.
   * This only owns chrome phases (leave questions → stage-complete Rive → callback).
   *
   * For LeaveToReview, the page stashed Leave in leaveAfterAnimationRef;
   * onLeaveToReview reads that ref and calls leaveQuestionnaire — we never see answers here.
   */
  function onStageFinished(payload: StageFinishedPayload) {
    // Edit / hub: skip Rive; advance or leave immediately
    if (isEditFromReview) {
      if (payload.type === StageFinishedType.LeaveToReview) {
        onLeaveToReviewRef.current()
        return
      }
      onAdvanceToStageRef.current(payload.nextStageIndex)
      return
    }

    /**
     * Full questionnaire: stash what to do after Rive ends, then:
     * QuestionsLeaving (slide out) → StageComplete (Rive) → pending callback.
     */
    pendingAfterCompletionRiveRef.current = () => {
      if (payload.type === StageFinishedType.LeaveToReview) {
        setBodyPhase(QuestionnaireBodyPhase.Questions)
        onLeaveToReviewRef.current()
        return
      }
      onAdvanceToStageRef.current(payload.nextStageIndex)
      const nextStageId = stagesRef.current?.[payload.nextStageIndex]?.id
      if (nextStageId && shouldPlayStageIntro(nextStageId)) {
        beginStageIntro(nextStageId)
        return
      }
      setBodyPhase(QuestionnaireBodyPhase.Questions)
    }

    clearRiveStartTimeout()
    setBodyPhase(QuestionnaireBodyPhase.QuestionsLeaving)
    pendingRiveStartTimeoutRef.current = setTimeout(() => {
      pendingRiveStartTimeoutRef.current = null
      // Only advance to StageComplete if we are still leaving (back may have cancelled)
      setBodyPhase((prevPhase) => {
        if (prevPhase !== QuestionnaireBodyPhase.QuestionsLeaving) return prevPhase
        return QuestionnaireBodyPhase.StageComplete
      })
    }, STAGE_COMPLETE_RIVE_START_AFTER_MS)
  }

  return {
    bodyPhase,
    stageIntroRiveSrc,
    stageIntroStageId,
    isShowingStageIntro: bodyPhase === QuestionnaireBodyPhase.StageIntro,
    isShowingStageComplete: bodyPhase === QuestionnaireBodyPhase.StageComplete,
    isLeavingQuestions: bodyPhase === QuestionnaireBodyPhase.QuestionsLeaving,
    onStageFinished,
    cancelAnimations,
    onStageIntroComplete,
    onStageCompleteRiveDone
  }
}
